import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Checkbox,
    ListItemText,
    Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

interface MassCancellationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (startDate: Date, endDate: Date, boardIds: string[], reason: string, startTime?: string, endTime?: string) => Promise<void>;
    boards: { id: string; name: string }[];
}

const MassCancellationDialog: React.FC<MassCancellationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    boards
}) => {
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [startTime, setStartTime] = useState<string>('');
    const [endTime, setEndTime] = useState<string>('');
    const [selectedBoards, setSelectedBoards] = useState<string[]>([]);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        if (!startDate || !endDate) {
            setError('Por favor selecciona un rango de fechas válido');
            return;
        }
        if (startDate > endDate) {
            setError('La fecha de inicio no puede ser posterior a la fecha de fin');
            return;
        }
        if (!reason.trim()) {
            setError('Por favor ingresa un motivo para la cancelación');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await onConfirm(startDate, endDate, selectedBoards, reason, startTime, endTime);
            onClose();
        } catch (err) {
            setError('Error al procesar la cancelación masiva');
        } finally {
            setLoading(false);
        }
    };

    const handleBoardChange = (event: any) => {
        const {
            target: { value },
        } = event;
        setSelectedBoards(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Cancelación Masiva de Turnos</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <Alert severity="warning">
                        Esta acción cancelará todos los turnos en el rango seleccionado. Esta acción no se puede deshacer fácilmente.
                    </Alert>

                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <DatePicker
                                label="Fecha Inicio"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                            <DatePicker
                                label="Fecha Fin"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Box>
                    </LocalizationProvider>

                    <FormControl fullWidth>
                        <InputLabel>Agendas (Opcional - Dejar vacío para todas)</InputLabel>
                        <Select
                            multiple
                            value={selectedBoards}
                            onChange={handleBoardChange}
                            renderValue={(selected) => {
                                if (selected.length === 0) return 'Todas las agendas';
                                return selected.map(id => boards.find(b => b.id === id)?.name).join(', ');
                            }}
                        >
                            {boards.map((board) => (
                                <MenuItem key={board.id} value={board.id}>
                                    <Checkbox checked={selectedBoards.indexOf(board.id) > -1} />
                                    <ListItemText primary={board.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Motivo de cancelación"
                        multiline
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Ej: Mantenimiento del local, Feriado, etc."
                        fullWidth
                        required
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                <Button
                    onClick={handleConfirm}
                    color="error"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? 'Procesando...' : 'Confirmar Cancelación'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MassCancellationDialog;
